import React from "react";
import { useTranslation } from "react-i18next";
import { openPaySheet } from "@/components/pay/PaySheet";
import { useResumeJob } from "@/components/pay/useResumeJob";
import { useUserCredits, refreshUserCredits } from "@/hooks/useUserCredits";
import {
  publishAdBrief, useAdBriefEvents,
  publishStudioProgress, publishStudioResult, publishStudioStills,
  requestAdCommit, requestAdInputsPatch,
} from "@/components/landing/director-studio/studioBus";
import type {
  AdAspect, AdDuration, AdInputs, AdLanguage, AdMusicMode, AdOffer, AdPlan, AdStillAspect, AdVoiceMode,
} from "@/components/landing/director-studio/types";
import {
  ADS_FALLBACK_CONFIG, adsAuthHeaders, adsCreditsFor, adsErrorMessage, fetchAdsConfig, uploadAdFile,
  type AdsConfig, type AdsSavedVoice, type AdsStockVoice,
} from "@/lib/ads";
import FaceLockPicker from "@/components/facelock/FaceLockPicker";
import FaceLockCastStep from "@/components/facelock/FaceLockCastStep";
import { useFaceLockCrew } from "@/components/facelock/useFaceLockCrew";
import { ShareCard } from "@/features/reel/ShareCard";
import { takeReelHandoff, toFileList } from "@/features/reel/handoff";
import SiteMarkSwitch from "@/components/pay/SiteMarkSwitch";
import { keepSiteMark } from "@/lib/site-mark";
import {
  castableAvatars, clearFaceLockWait, readFaceLockWait, saveFaceLockWait, type FaceLockAvatar,
} from "@/lib/facelock";

// Commercials & Ads flow INSIDE the Director chat console.
//
// Mirrors the Story tool (docs/ads-tool-contract.md §2 + §7):
//
//   write    -> offer, duration, aspect, brief, product photos, brand, feature-me,
//               voice (none / stock / clone), music, captions — all in one card
//   planning -> POST /api/web/ads/plan (free, no DB row, no charge)
//   review   -> the plan lands on the STORYBOARD CANVAS as AdBriefCard. The user
//               edits every beat there and presses Generate on the card.
//   working  -> POST /api/web/ads/generate charges ONCE and submits ONE worker
//               job; GET /status polls every 3.5 s into a progress node
//   done     -> player node (video) + stills grid node (image ads)
//
// PRODUCT LOCK: product photos are uploaded as-is and referenced by index. The
// worker composites those exact pixels; nothing in this flow asks the engine
// to draw a product. Credits are never hardcoded: GET /config is the source.

type Phase = "write" | "planning" | "review" | "working" | "done";

const C = {
  bg: "#05070b",
  card: "#101722",
  border: "#2a3648",
  green: "#22c55e",
  greenDeep: "#15803d",
  gold: "#e3b341",
  white: "#ffffff",
  offwhite: "#e2e8f0",
  muted: "#94a3b8",
  red: "#f87171",
};

const POLL_MS = 3500;
/** What an ad waiting for its new avatar keeps (lib/facelock saveFaceLockWait): the
 *  whole write card, every file in it already an uploaded URL. */
type AdsWaitChoices = { inputs?: AdInputs };
const MAX_PRODUCT_PHOTOS = 4;
const MIN_SELFIES = 4;
const MAX_SELFIES = 6;
const MIN_BRIEF = 20;

const defaultInputs = (): AdInputs => ({
  offer: "spot",
  duration_s: 30,
  aspect: "9:16",
  brief: "",
  product: { name: "", photos: [] },
  brand: { name: "", logo_url: null, colors: [], tagline: "", price: "", cta: "", url: "" },
  avatar: null,
  voice: { mode: "none", voice_id: "", saved_voice_id: "", sample_url: null, language: "en", avatar_speaks: false },
  music: { mode: "none", url: null, track_id: null },
  captions: true,
  still_count: 8,
  still_aspects: ["1:1", "9:16"],
});

type StatusPayload = {
  jobId?: string;
  status?: "queued" | "processing" | "completed" | "failed";
  stage?: string | null;
  progress?: number | null;
  message?: string | null;
  video?: { url: string; seconds?: number; width?: number; height?: number } | null;
  stills?: Array<{ index: number; url: string; aspect: string; width?: number; height?: number }>;
  error?: string | null;
  credits_charged?: number;
};

/** Duration of an audio file via an off-screen <audio>; null when the browser
 *  cannot tell (the server re-checks with ffprobe either way). */
function readAudioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const audio = document.createElement("audio");
      let settled = false;
      const done = (v: number | null) => {
        if (settled) return;
        settled = true;
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        resolve(v);
      };
      const timer = window.setTimeout(() => done(null), 4000);
      audio.preload = "metadata";
      audio.onloadedmetadata = () => { window.clearTimeout(timer); done(Number.isFinite(audio.duration) ? audio.duration : null); };
      audio.onerror = () => { window.clearTimeout(timer); done(null); };
      audio.src = url;
    } catch {
      resolve(null);
    }
  });
}

/** Kokoro ids encode the gender in the second letter (af_heart, am_michael,
 *  ef_dora, em_alex). Used only for the female/male quick-action chips. */
function stockVoiceByGender(stock: AdsStockVoice[], language: AdLanguage, gender: "f" | "m"): AdsStockVoice | null {
  const re = new RegExp(`^[a-z]${gender}_`);
  return stock.find((v) => v.language === language && re.test(v.id)) || null;
}

const pillStyle = (on: boolean, disabled = false): React.CSSProperties => ({
  background: on ? "rgba(34,197,94,.16)" : "rgba(255,255,255,.06)",
  border: `1px solid ${on ? C.green : C.border}`,
  borderRadius: 999,
  color: on ? C.white : C.offwhite,
  cursor: disabled ? "default" : "pointer",
  fontSize: 13,
  fontWeight: on ? 700 : 500,
  opacity: disabled ? 0.5 : 1,
  padding: "6px 12px",
  whiteSpace: "nowrap",
});

const inputStyle: React.CSSProperties = {
  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, boxSizing: "border-box",
  color: C.offwhite, fontSize: 13, padding: "8px 10px", width: "100%",
};

const smallBtn: React.CSSProperties = {
  background: "rgba(255,255,255,.06)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.offwhite,
  cursor: "pointer", fontSize: 12, padding: "6px 10px",
};

function Section({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} style={{ display: "grid", gap: 6, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>{title}</div>
      {children}
    </div>
  );
}

function Pills<T extends string | number>({ value, options, onChange, testPrefix, disabled, label }: {
  value: T; options: Array<{ id: T; label: string }>; onChange: (v: T) => void; testPrefix: string; disabled?: boolean; label: (id: T) => string;
}) {
  return (
    <div role="group" aria-label={testPrefix} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((o) => (
        <button key={String(o.id)} type="button" disabled={disabled} data-testid={`${testPrefix}-${String(o.id)}`}
          aria-pressed={o.id === value} onClick={() => onChange(o.id)} style={pillStyle(o.id === value, disabled)}>
          {label(o.id)}
        </button>
      ))}
    </div>
  );
}

function Thumbs({ urls, onRemove, disabled, testPrefix }: { urls: string[]; onRemove: (i: number) => void; disabled: boolean; testPrefix: string }) {
  if (!urls.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {urls.map((u, i) => (
        <div key={`${u}-${i}`} data-testid={`${testPrefix}-${i}`} style={{ position: "relative", width: 56, height: 56, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`, background: C.bg }}>
          <img src={u} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          {!disabled && (
            <button type="button" aria-label="remove" onClick={() => onRemove(i)}
              style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: 9, border: "none", background: "rgba(0,0,0,.7)", color: C.white, fontSize: 11, cursor: "pointer", lineHeight: "18px", padding: 0 }}>
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdsChatFlow() {
  const { t } = useTranslation();
  const [phase, setPhase] = React.useState<Phase>("write");
  const [note, setNote] = React.useState("");
  const [inputs, setInputs] = React.useState<AdInputs>(defaultInputs);
  const [config, setConfig] = React.useState<AdsConfig>(ADS_FALLBACK_CONFIG);
  const [voices, setVoices] = React.useState<{ stock: AdsStockVoice[]; saved: AdsSavedVoice[] }>({ stock: [], saved: [] });
  // This account's own trained faces (GET /api/web/ads/artists), newest last.
  const [artists, setArtists] = React.useState<Array<{ slug: string; latest: boolean }>>([]);
  const latestArtist = React.useMemo(() => (artists.find((a) => a.latest) || artists[artists.length - 1] || { slug: "" }).slug, [artists]);
  const [featureMe, setFeatureMe] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [consent, setConsent] = React.useState(false);
  const [voiceName, setVoiceName] = React.useState("");
  const [sampleSeconds, setSampleSeconds] = React.useState<number | null>(null);
  const [recording, setRecording] = React.useState<{ seconds: number } | null>(null);
  const [musicName, setMusicName] = React.useState("");
  const [videoUrl, setVideoUrl] = React.useState("");
  const [stills, setStills] = React.useState<Array<{ index: number; url: string; aspect: string }>>([]);
  const [jobId, setJobId] = React.useState<string | null>(null);

  const loggedIn = (() => { try { return !!localStorage.getItem("auth_token"); } catch { return false; } })();
  const { balance } = useUserCredits(loggedIn);
  // FaceLock crew: with a castable FaceLock the picker replaces the old
  // trained-face <select>, and a chosen FaceLock stands in for the selfies
  // (the server resolves avatar.avatar_id into its close-ups and its face).
  // An empty list means the feature or its picker is off: the flow below is
  // then exactly what it was.
  //
  // REQUIRED (the server's `required`, FACELOCK_CREW_REQUIRED, owner rule
  // 2026-09-30): a person in the ad comes from an avatar or not at all. "Put
  // me in the ad" then shows the avatar step (pick one, or create one right
  // here) instead of the selfie uploader, and an ad waiting for a new avatar
  // keeps its whole write card and plans by itself when the avatar is ready
  // while the page is open.
  const { status: crewStatus, crew, refresh: refreshCrew } = useFaceLockCrew(loggedIn, { poll: "required" });
  const castableFaceLocks = React.useMemo(() => castableAvatars(crew), [crew]);
  const faceLockRequired = crewStatus === "on" && crew?.required === true;
  const faceLockPickerOn = castableFaceLocks.length > 0;
  const chosenFaceLock = faceLockPickerOn && inputs.avatar?.avatar_id
    ? castableFaceLocks.find((a) => a.id === inputs.avatar?.avatar_id) || null
    : null;
  /** The avatar this ad is waiting for (created here, still training). */
  const [waitingFaceLockId, setWaitingFaceLockId] = React.useState<string | null>(null);
  /** An awaited avatar that did not finish: the step says so and offers to create another. */
  const [failedFaceLockId, setFailedFaceLockId] = React.useState<string | null>(null);
  /** Plan by itself once ready: the avatar was created here, or seen training while the page was open. */
  const waitArmed = React.useRef(false);
  const phaseRef = React.useRef<Phase>("write");
  phaseRef.current = phase;
  // Selfies uploaded before a FaceLock was picked come back with "New photos".
  const selfieStash = React.useRef<string[]>([]);
  const timers = React.useRef<number[]>([]);
  const recRef = React.useRef<{ rec: MediaRecorder; chunks: Blob[]; started: number; stream: MediaStream; tick: number } | null>(null);
  const inputsRef = React.useRef(inputs);
  inputsRef.current = inputs;

  const credits = adsCreditsFor(config, inputs.offer, inputs.duration_s, inputs.still_count);

  const track = (id: number) => { timers.current.push(id); return id; };
  const stopPolling = React.useCallback(() => {
    timers.current.forEach((id) => window.clearInterval(id));
    timers.current = [];
  }, []);

  // ---- boot: config (prices), voices, optional trained artists, resume ----
  React.useEffect(() => {
    let alive = true;
    void fetchAdsConfig().then(({ config: c }) => { if (alive) setConfig(c); });
    if (!loggedIn) return () => { alive = false; };
    void (async () => {
      try {
        const r = await fetch("/api/web/ads/voices", { headers: adsAuthHeaders(false) });
        if (r.ok) {
          const j = await r.json();
          if (alive) setVoices({ stock: Array.isArray(j.stock) ? j.stock : [], saved: Array.isArray(j.saved) ? j.saved : [] });
        }
      } catch { /* the voice select stays empty; stock voices also arrive with /config */ }
      try {
        // The account's trained faces. The newest is preselected when "Feature
        // me" is switched on; the server only ever accepts one this account owns.
        const r = await fetch("/api/web/ads/artists", { headers: adsAuthHeaders(false) });
        if (r.ok) {
          const j = await r.json();
          const list = Array.isArray(j && j.artists) ? j.artists : [];
          if (alive) {
            setArtists(list.filter((a: { slug?: string }) => typeof a.slug === "string" && a.slug)
              .map((a: { slug: string; latest?: boolean }) => ({ slug: a.slug, latest: Boolean(a.latest) })));
          }
        }
      } catch { /* no trained faces: the photos alone carry the identity */ }
      try {
        // Board resume: an ad still rendering from a previous visit reconnects.
        const r = await fetch("/api/web/ads/runs", { headers: adsAuthHeaders(false) });
        if (r.ok) {
          const j = await r.json();
          const runs: StatusPayload[] = Array.isArray(j.runs) ? j.runs : [];
          const live = runs.find((run) => run.status === "queued" || run.status === "processing");
          if (alive && live?.jobId) {
            setJobId(live.jobId);
            setPhase("working");
            setNote(t("ads.resumedRun"));
            startPolling(live.jobId, null);
          }
        }
      } catch { /* nothing to resume */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  React.useEffect(() => () => {
    stopPolling();
    const rec = recRef.current;
    if (rec) {
      try { rec.rec.stop(); } catch { /* noop */ }
      rec.stream.getTracks().forEach((tr) => tr.stop());
      window.clearInterval(rec.tick);
    }
    publishAdBrief(null);
    publishStudioProgress("ads", null);
    publishStudioResult("ads", null);
    publishStudioStills(null);
  }, [stopPolling]);

  // /voices carries each stock voice's `note` ("Female, warm"); /config's list
  // does not (§9). Prefer /voices once it has answered; config is the fallback
  // so the picker is never empty while /voices is still loading.
  const allStock = voices.stock.length ? voices.stock : config.voice.stock;
  const stockVoices = allStock.filter((v) => v.language === inputs.voice.language);

  // ---- validation of the write card ----
  const briefOk = inputs.brief.trim().length >= MIN_BRIEF;
  // REQUIRED: a person in the ad only through a chosen avatar, never selfies.
  const selfiesOk = !featureMe || (inputs.avatar !== null && (chosenFaceLock !== null
    || (!faceLockRequired && inputs.avatar.selfies.length >= MIN_SELFIES && inputs.avatar.selfies.length <= MAX_SELFIES)));
  const voiceOk = inputs.voice.mode === "none"
    || (inputs.voice.mode === "stock" && !!inputs.voice.voice_id)
    || (inputs.voice.mode === "clone" && (!!inputs.voice.sample_url || !!inputs.voice.saved_voice_id) && consent);
  const musicOk = inputs.music.mode === "none" || (inputs.music.mode === "upload" && !!inputs.music.url) || (inputs.music.mode === "stock" && !!inputs.music.track_id);
  const stillsOk = inputs.offer === "spot" || inputs.still_aspects.length > 0;
  const ready = briefOk && selfiesOk && voiceOk && musicOk && stillsOk && !busy;
  const readyRef = React.useRef(ready);
  readyRef.current = ready;
  const blocker = !briefOk ? t("ads.needBrief", { count: MIN_BRIEF })
    : !selfiesOk ? (faceLockRequired ? t("faceLock.required.adsNeedAvatar") : t("ads.needSelfies", { min: MIN_SELFIES, max: MAX_SELFIES }))
    : inputs.voice.mode === "stock" && !inputs.voice.voice_id ? t("ads.needStockVoice")
    : inputs.voice.mode === "clone" && !(inputs.voice.sample_url || inputs.voice.saved_voice_id) ? t("ads.needVoiceSample")
    : inputs.voice.mode === "clone" && !consent ? t("ads.needConsent")
    : !musicOk ? t("ads.needMusic")
    : !stillsOk ? t("ads.needStillAspect")
    : "";

  // ---- uploads ----
  const upload = async (files: FileList | null, type: "ad_product" | "ad_logo" | "ad_selfie", max: number, current: string[]): Promise<string[]> => {
    if (!files || !files.length) return current;
    const room = Math.max(0, max - current.length);
    const list = Array.from(files).slice(0, room);
    if (!list.length) return current;
    setBusy(t("ads.uploadingPhotos"));
    const out = [...current];
    try {
      for (const f of list) out.push(await uploadAdFile(t, f, type));
      setNote("");
    } catch (e: unknown) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
    return out;
  };

  const onProductPhotos = async (files: FileList | null) => {
    const photos = await upload(files, "ad_product", MAX_PRODUCT_PHOTOS, inputs.product.photos);
    setInputs((p) => ({ ...p, product: { ...p.product, photos } }));
  };
  // The product photo picked in the Reel's "Make one like this" sheet
  // (features/reel), and the spot length of the output it copies.
  const reelRef = React.useRef(false);
  React.useEffect(() => {
    if (reelRef.current) return;
    reelRef.current = true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "reel") return;
    const seconds = Number(params.get("length"));
    if (config.durations.includes(seconds as AdDuration)) setInputs((p) => ({ ...p, duration_s: seconds as AdDuration }));
    const handoff = takeReelHandoff("ads");
    if (handoff?.files?.length) void onProductPhotos(toFileList(handoff.files));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onSelfies = async (files: FileList | null) => {
    const selfies = await upload(files, "ad_selfie", MAX_SELFIES, inputs.avatar?.selfies || []);
    setInputs((p) => ({ ...p, avatar: { selfies, artist_slug: p.avatar ? p.avatar.artist_slug : latestArtist } }));
  };
  const onLogo = async (files: FileList | null) => {
    const urls = await upload(files, "ad_logo", 1, []);
    if (urls[0]) setInputs((p) => ({ ...p, brand: { ...p.brand, logo_url: urls[0] } }));
  };
  const onMusicFile = async (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    setBusy(t("ads.uploadingMusic"));
    try {
      const url = await uploadAdFile(t, f, "audio");
      setMusicName(f.name);
      setInputs((p) => ({ ...p, music: { mode: "upload", url, track_id: null } }));
      setNote("");
    } catch (e: unknown) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  // ---- voice clone: record in-browser, or upload a file ----
  const acceptSample = async (file: File, seconds: number | null) => {
    const min = config.voice.clone_min_seconds;
    const max = config.voice.clone_max_seconds;
    if (seconds !== null && seconds < min) { setNote(t("ads.sampleTooShort", { min })); return; }
    if (seconds !== null && seconds > max) { setNote(t("ads.sampleTooLong", { max })); return; }
    setBusy(t("ads.uploadingSample"));
    try {
      const url = await uploadAdFile(t, file, "ad_voice");
      setSampleSeconds(seconds);
      setInputs((p) => ({ ...p, voice: { ...p.voice, mode: "clone", sample_url: url, saved_voice_id: "" } }));
      setNote("");
    } catch (e: unknown) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const canRecord = typeof window !== "undefined" && typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  const stopRecording = React.useCallback(() => {
    const cur = recRef.current;
    if (!cur) return;
    try { if (cur.rec.state !== "inactive") cur.rec.stop(); } catch { /* noop */ }
  }, []);

  const startRecording = async () => {
    if (!canRecord || recRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => {
        try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
      });
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const started = Date.now();
      const entry = { rec, chunks: [] as Blob[], started, stream, tick: 0 };
      rec.ondataavailable = (e: BlobEvent) => { if (e.data && e.data.size) entry.chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        window.clearInterval(entry.tick);
        recRef.current = null;
        setRecording(null);
        const seconds = Math.round(((Date.now() - started) / 1000) * 10) / 10;
        const type = rec.mimeType || mime || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File(entry.chunks, `voice-sample.${ext}`, { type });
        void acceptSample(file, seconds);
      };
      entry.tick = window.setInterval(() => {
        const s = Math.floor((Date.now() - started) / 1000);
        setRecording({ seconds: s });
        if (s >= config.voice.clone_max_seconds) stopRecording();
      }, 250);
      recRef.current = entry;
      setRecording({ seconds: 0 });
      setNote("");
      rec.start(250);
    } catch {
      setNote(t("ads.micDenied"));
    }
  };

  const onSampleFile = async (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    const seconds = await readAudioDuration(f);
    await acceptSample(f, seconds);
  };

  const saveVoice = async () => {
    if (!inputs.voice.sample_url || !consent) return;
    setBusy(t("ads.savingVoice"));
    try {
      const r = await fetch("/api/web/ads/voice", {
        method: "POST", headers: adsAuthHeaders(),
        body: JSON.stringify({ sample_url: inputs.voice.sample_url, name: voiceName.trim() || t("ads.myVoice"), language: inputs.voice.language, consent: true }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.id) { setNote(adsErrorMessage(t, r.status, j, "ads.voiceSaveFailed")); return; }
      const saved: AdsSavedVoice = { id: j.id, name: j.name, language: j.language, sample_url: j.sample_url };
      setVoices((v) => ({ ...v, saved: [saved, ...v.saved.filter((s) => s.id !== saved.id)] }));
      setInputs((p) => ({ ...p, voice: { ...p.voice, saved_voice_id: saved.id, sample_url: saved.sample_url } }));
      setNote(t("ads.voiceSaved"));
    } catch (e: unknown) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const pickSavedVoice = (id: string) => {
    if (!id) {
      setInputs((p) => ({ ...p, voice: { ...p.voice, saved_voice_id: "", sample_url: null } }));
      setSampleSeconds(null);
      return;
    }
    const v = voices.saved.find((s) => s.id === id);
    if (!v) return;
    setInputs((p) => ({ ...p, voice: { ...p.voice, mode: "clone", saved_voice_id: v.id, sample_url: v.sample_url, language: (v.language === "es" ? "es" : "en") } }));
    setSampleSeconds(null);
  };

  // ---- Phase 1: plan (free) ----
  const plan = React.useCallback(async (steer = "") => {
    stopPolling();
    // An ad that waited for its avatar is on its way: nothing left to keep.
    clearFaceLockWait("ads");
    setWaitingFaceLockId(null);
    waitArmed.current = false;
    const body: AdInputs = { ...inputsRef.current, note: steer };
    setPhase("planning");
    setNote(t("ads.planning"));
    publishAdBrief(null);
    publishStudioProgress("ads", { title: t("ads.planningTitle"), message: t("ads.planning"), progress: null });
    try {
      const r = await fetch("/api/web/ads/plan", { method: "POST", headers: adsAuthHeaders(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      publishStudioProgress("ads", null);
      if (!r.ok || !j.plan) {
        setPhase("write");
        setNote(adsErrorMessage(t, r.status, j, "ads.couldNotPlan"));
        // Refused for want of an avatar (the rule switched on since this page
        // loaded): read the crew again so the avatar step shows.
        if (j && j.error === "facelock_required") void refreshCrew();
        return;
      }
      const planned = j.plan as AdPlan;
      publishAdBrief({
        plan: planned,
        inputs: body,
        credits: typeof j.credits === "number" ? j.credits : credits,
        planId: typeof j.planId === "string" ? j.planId : null,
        estimatedMinutes: typeof j.estimated_minutes === "number" ? j.estimated_minutes : null,
        planner: j.planner === "fallback" ? "fallback" : j.planner === "grok" ? "grok" : null,
      });
      setPhase("review");
      setNote("");
    } catch (e: unknown) {
      publishStudioProgress("ads", null);
      setPhase("write");
      setNote(e instanceof Error ? e.message : String(e));
    }
  }, [credits, stopPolling, t, refreshCrew]);

  // ---- Phase 2/3: render the approved plan, then poll ----
  const startPolling = React.useCallback((id: string, fallback: { plan: AdPlan; inputs: AdInputs } | null) => {
    stopPolling();
    const poll = track(window.setInterval(async () => {
      try {
        const r = await fetch(`/api/web/ads/status?id=${encodeURIComponent(id)}`, { headers: adsAuthHeaders(false) });
        const s: StatusPayload = await r.json();
        if (!r.ok) {
          if (r.status === 404) {
            window.clearInterval(poll);
            publishStudioProgress("ads", null);
            setPhase(fallback ? "review" : "write");
            setNote(t("ads.jobNotFound"));
          }
          return;
        }
        if (s.status === "completed") {
          window.clearInterval(poll);
          setPhase("done");
          publishAdBrief(null);
          publishStudioProgress("ads", null);
          refreshUserCredits();
          const video = s.video && s.video.url ? s.video : null;
          const list = Array.isArray(s.stills) ? s.stills.filter((st) => st && st.url) : [];
          if (video) {
            setVideoUrl(video.url);
            publishStudioResult("ads", { title: t("ads.resultTitle"), url: video.url });
          }
          if (list.length) {
            setStills(list.map((st) => ({ index: st.index, url: st.url, aspect: st.aspect })));
            publishStudioStills({ service: "ads", title: t("ads.stillsTitle", { count: list.length }), items: list.map((st) => ({ url: st.url, aspect: st.aspect, index: st.index })) });
          }
          setNote(video && list.length ? t("ads.bothReady") : video ? t("ads.videoReady") : list.length ? t("ads.stillsReady") : t("ads.savedToGallery"));
          return;
        }
        if (s.status === "failed") {
          window.clearInterval(poll);
          const code = s.error ? t(`ads.errors.${s.error}`, { defaultValue: s.error }) : t("ads.renderFailed");
          setNote(`${code} ${t("ads.renderFailedTail")}`);
          publishStudioProgress("ads", null);
          refreshUserCredits();
          if (fallback) {
            publishAdBrief({ plan: fallback.plan, inputs: fallback.inputs, credits });
            setPhase("review");
          } else {
            setPhase("write");
          }
          return;
        }
        const stageLabel = s.stage ? t(`ads.stage.${s.stage}`, { defaultValue: s.stage }) : t("ads.stage.queued");
        const message = s.message || stageLabel;
        setNote(message);
        publishStudioProgress("ads", {
          title: t("ads.renderingTitle"),
          message: s.stage ? `${stageLabel} · ${s.message || ""}`.replace(/ · $/, "") : message,
          progress: typeof s.progress === "number" && Number.isFinite(s.progress) && s.progress > 0 ? s.progress : null,
        });
      } catch { /* keep polling */ }
    }, POLL_MS));
  }, [credits, stopPolling, t]);

  const render = React.useCallback(async (approved: AdPlan, withInputs: AdInputs) => {
    stopPolling();
    setPhase("working");
    setNote(t("ads.startingRender"));
    setVideoUrl("");
    setStills([]);
    publishAdBrief({ plan: approved, inputs: withInputs, credits, rendering: true });
    publishStudioResult("ads", null);
    publishStudioStills(null);
    publishStudioProgress("ads", { title: t("ads.renderingTitle"), message: t("ads.startingRender"), progress: null });
    try {
      const r = await fetch("/api/web/ads/generate", {
        method: "POST", headers: adsAuthHeaders(),
        body: JSON.stringify({ ...withInputs, plan: approved, watermark: keepSiteMark() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.jobId) {
        setPhase("review");
        publishAdBrief({ plan: approved, inputs: withInputs, credits });
        publishStudioProgress("ads", null);
        setNote(adsErrorMessage(t, r.status, j, "ads.couldNotStartRender"));
        return;
      }
      refreshUserCredits(); // charged at launch
      setJobId(j.jobId);
      setNote(t("ads.renderStarted", { credits: typeof j.credits_charged === "number" ? j.credits_charged : credits }));
      startPolling(j.jobId, { plan: approved, inputs: withInputs });
    } catch (e: unknown) {
      setPhase("review");
      publishAdBrief({ plan: approved, inputs: withInputs, credits });
      publishStudioProgress("ads", null);
      setNote(e instanceof Error ? e.message : String(e));
    }
  }, [credits, startPolling, stopPolling, t]);

  // BACK FROM CHECKOUT (Studio Blueprint 04): the plan and inputs the card
  // held when the buyer paid come back; once this flow's balance shows the
  // new credits, the same ad starts. A cancel (or a payment still clearing)
  // puts the plan back for another look.
  const [resumeAd, setResumeAd] = React.useState<{ plan: AdPlan; inputs: AdInputs } | null>(null);
  const putBack = (p: { plan: AdPlan; inputs: AdInputs }) => {
    setInputs(p.inputs);
    publishAdBrief({ plan: p.plan, inputs: p.inputs, credits: adsCreditsFor(config, p.inputs.offer, p.inputs.duration_s, p.inputs.still_count) });
    setPhase("review");
  };
  useResumeJob("ads", loggedIn && balance !== null, {
    start: (job) => {
      const p = job.payload as { plan?: AdPlan; inputs?: AdInputs } | undefined;
      if (!p?.plan || !p.inputs) return;
      putBack({ plan: p.plan, inputs: p.inputs });
      setResumeAd({ plan: p.plan, inputs: p.inputs });
    },
    restore: (job) => {
      const p = job.payload as { plan?: AdPlan; inputs?: AdInputs } | undefined;
      if (p?.plan && p.inputs) putBack({ plan: p.plan, inputs: p.inputs });
    },
  });
  React.useEffect(() => {
    // `credits` follows the restored inputs; render only once they agree and
    // the balance covers them, or the render would be refused for credits.
    if (!resumeAd || balance === null || inputs !== resumeAd.inputs || balance < credits) return;
    setResumeAd(null);
    void render(resumeAd.plan, resumeAd.inputs);
  }, [resumeAd, balance, inputs, credits, render]);

  const cancel = async () => {
    if (!jobId) return;
    try {
      await fetch("/api/web/ads/cancel", { method: "POST", headers: adsAuthHeaders(), body: JSON.stringify({ jobId }) });
    } catch { /* the poll reports the final state */ }
  };

  // The card is where the user acts; those intents land here because this is
  // the one place that touches the API and their credits.
  useAdBriefEvents({
    onRender: render,
    onReplan: (steer) => { if (inputsRef.current.brief.trim()) void plan(steer); },
    onDiscard: () => {
      stopPolling();
      publishAdBrief(null);
      publishStudioProgress("ads", null);
      setPhase("write");
      setNote(t("ads.boardDiscarded"));
    },
  });

  /** Picker -> inputs: a FaceLock replaces the selfies; null = new photos. */
  const pickFaceLock = (id: string | null) => {
    const cur = inputsRef.current.avatar;
    if (id) {
      if (cur && !cur.avatar_id) selfieStash.current = cur.selfies;
      setInputs((p) => ({ ...p, avatar: { selfies: [], artist_slug: "", avatar_id: id } }));
    } else {
      setInputs((p) => ({ ...p, avatar: { selfies: p.avatar?.avatar_id ? selfieStash.current : (p.avatar?.selfies || []), artist_slug: "" } }));
    }
  };

  // ---- REQUIRED: an ad waiting for a new avatar -----------------------------
  /** Stop waiting for an avatar: the ad was planned, or the customer went another way. */
  const stopFaceLockWait = () => {
    clearFaceLockWait("ads");
    setWaitingFaceLockId(null);
    waitArmed.current = false;
  };

  /** A new avatar was just created in the avatar step: keep the ad and wait for it. */
  const onFaceLockCreated = (avatar: FaceLockAvatar | null) => {
    void refreshCrew();
    if (!avatar) return;
    setFailedFaceLockId(null);
    setInputs((p) => ({ ...p, avatar: { selfies: [], artist_slug: "", avatar_id: avatar.id } }));
    setWaitingFaceLockId(avatar.id);
    waitArmed.current = true;
  };

  // The whole write card is kept in this browser while it waits (every file in
  // it is already an uploaded URL), so leaving and coming back loses nothing.
  React.useEffect(() => {
    if (!waitingFaceLockId) return;
    saveFaceLockWait<AdsWaitChoices>("ads", waitingFaceLockId, { inputs: { ...inputs, avatar: { selfies: [], artist_slug: "", avatar_id: waitingFaceLockId } } });
  }, [waitingFaceLockId, inputs]);

  // Coming back (or reloading) while an ad waited for its avatar: the card comes
  // back with that avatar selected. Still training -> it plans by itself when
  // ready; ready already -> selected, and the customer plans.
  const waitRestoreRef = React.useRef(false);
  React.useEffect(() => {
    if (waitRestoreRef.current || !loggedIn || (crewStatus !== "on" && crewStatus !== "off")) return;
    waitRestoreRef.current = true;
    const rec = readFaceLockWait<AdsWaitChoices>("ads");
    if (!rec) return;
    if (crewStatus === "off" || !crew?.required) { clearFaceLockWait("ads"); return; }
    if (phaseRef.current !== "write") return; // an ad already rendering owns this load
    const saved = rec.choices && rec.choices.inputs;
    setInputs((p) => ({ ...p, ...(saved && typeof saved === "object" ? saved : {}), avatar: { selfies: [], artist_slug: "", avatar_id: rec.avatarId } }));
    setFeatureMe(true);
    setWaitingFaceLockId(rec.avatarId);
    waitArmed.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, crewStatus, crew]);

  // The awaited avatar, as each read of the crew reports it.
  React.useEffect(() => {
    if (!waitingFaceLockId || !crew) return;
    const a = crew.avatars.find((x) => x.id === waitingFaceLockId);
    if (!a) return; // just created: the next read lists it
    if (a.castable) {
      const armed = waitArmed.current;
      waitArmed.current = false;
      if (!armed) { setWaitingFaceLockId(null); setNote(t("faceLock.required.adsResumed", { name: a.name })); return; }
      if (phaseRef.current === "write" && readyRef.current) {
        setNote(t("faceLock.required.adsPlanning", { name: a.name }));
        void plan();
      } else {
        setWaitingFaceLockId(null);
        setNote(t("faceLock.required.adsReadyFinish", { name: a.name }));
      }
      return;
    }
    if (a.status === "failed") {
      stopFaceLockWait();
      setFailedFaceLockId(a.id);
      setInputs((p) => ({ ...p, avatar: { selfies: [], artist_slug: "" } }));
      return;
    }
    // Training while this page is open: plan by itself once it is ready.
    waitArmed.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crew, waitingFaceLockId]);

  const setNarrator = (gender: "f" | "m") => {
    const v = stockVoiceByGender(allStock, inputs.voice.language, gender);
    if (!v) return;
    const voice: AdInputs["voice"] = { ...inputs.voice, mode: "stock", voice_id: v.id, saved_voice_id: "", sample_url: null };
    setInputs((p) => ({ ...p, voice }));
    requestAdInputsPatch({ voice });
  };

  if (!loggedIn) {
    return (
      <div className="text-[15px] leading-[1.55] sm:text-sm" data-testid="ads-signin">
        <p className="mb-3">{t("ads.signInPrompt")}</p>
        {/* Back to this stop after signing in: a bare / opens on the welcome
            cards now (2026-09-24), not the studio this link was pressed in.
            44px tall and 15px on a phone (was 34px): it is the only way on.
            Full width below sm like the pitch CTAs, and balanced if a long
            label (Spanish at 320px) has to take two lines. 4px sides
            below 360px (2026-09-25) keep "Regístrate / Inicia sesión →"
            on one line at 320. */}
        <a href="/signup?redirect_to=%2F%3Ftool%3Dads" className="inline-flex min-h-[44px] w-full items-center justify-center text-balance rounded-md bg-primary px-5 py-2.5 text-center text-[15px] font-semibold text-primary-foreground active:scale-[0.98] max-[359px]:px-1 sm:w-auto sm:text-sm">{t("ads.signUpLogIn")}</a>
      </div>
    );
  }

  const set = <K extends keyof AdInputs>(k: K, v: AdInputs[K]) => setInputs((p) => ({ ...p, [k]: v }));
  const setBrand = (k: keyof AdInputs["brand"], v: string) => setInputs((p) => ({ ...p, brand: { ...p.brand, [k]: v } }));
  const hasSpot = inputs.offer !== "stills";
  const hasStills = inputs.offer !== "spot";

  return (
    <div data-testid="ads-flow" style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: "system-ui, sans-serif", fontSize: 13, color: C.offwhite }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 3, background: `linear-gradient(90deg,${C.green},${C.gold})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {t("ads.title")}
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${C.border}`, borderRadius: 999, padding: "4px 10px", background: "rgba(227,179,65,.08)" }}>
          <span data-testid="ads-balance" style={{ fontSize: 13, fontWeight: 800, color: C.gold, fontFamily: "'Rajdhani', sans-serif" }}>
            {balance === null ? "—" : balance.toLocaleString()}{t("ads.runCost", { cost: credits.toLocaleString() })}
          </span>
        </div>
      </div>

      {phase === "write" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, display: "grid", gap: 10 }}>
          <div style={{ fontSize: 12, color: C.offwhite, lineHeight: 1.6 }}>
            <b style={{ color: C.white }}>{t("ads.lead")}</b> {t("ads.leadBody")}
          </div>

          {/* Offer / duration / aspect */}
          <Pills value={inputs.offer} testPrefix="ads-offer" label={(id) => t(`ads.offers.${id}`)}
            options={config.offers.map((id) => ({ id, label: id }))} onChange={(v: AdOffer) => set("offer", v)} />
          <div style={{ fontSize: 12, color: C.muted }}>{t(`ads.offerDesc.${inputs.offer}`)}</div>
          {hasSpot && (
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, color: C.muted }}>{t("ads.duration")}</div>
              <Pills value={inputs.duration_s} testPrefix="ads-duration" label={(id) => t("ads.seconds", { seconds: id })}
                options={config.durations.map((id) => ({ id, label: String(id) }))} onChange={(v: AdDuration) => set("duration_s", v)} />
              <div style={{ fontSize: 12, color: C.muted }}>{t("ads.aspect")}</div>
              <Pills value={inputs.aspect} testPrefix="ads-aspect" label={(id) => t(`ads.aspects.${id.replace(":", "x")}`)}
                options={config.aspects.map((id) => ({ id, label: id }))} onChange={(v: AdAspect) => set("aspect", v)} />
            </div>
          )}
          {hasStills && (
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, color: C.muted }}>{t("ads.stillCount")}</div>
              <Pills value={inputs.still_count} testPrefix="ads-stills" label={(id) => t("ads.pictures", { count: id })}
                options={config.still_counts.map((id) => ({ id, label: String(id) }))} onChange={(v: 8 | 16) => set("still_count", v)} />
              <div style={{ fontSize: 12, color: C.muted }}>{t("ads.stillAspects")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {config.still_aspects.map((a) => {
                  const on = inputs.still_aspects.includes(a);
                  return (
                    <button key={a} type="button" data-testid={`ads-still-aspect-${a}`} aria-pressed={on} style={pillStyle(on)}
                      onClick={() => set("still_aspects", on ? inputs.still_aspects.filter((x) => x !== a) : [...inputs.still_aspects, a] as AdStillAspect[])}>
                      {t(`ads.aspects.${a.replace(":", "x")}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Brief */}
          <Section title={t("ads.briefTitle")}>
            <textarea value={inputs.brief} onChange={(e) => set("brief", e.target.value)} rows={4} data-testid="ads-brief"
              placeholder={t("ads.briefPlaceholder")} style={{ ...inputStyle, lineHeight: 1.6, resize: "vertical" }} />
          </Section>

          {/* Product — the exact pixels that will ship */}
          <Section title={t("ads.productTitle")} testId="ads-product">
            <input value={inputs.product.name} onChange={(e) => setInputs((p) => ({ ...p, product: { ...p.product, name: e.target.value } }))}
              placeholder={t("ads.productName")} data-testid="ads-product-name" style={inputStyle} />
            <div style={{ fontSize: 12, color: C.muted }}>{t("ads.productPhotosHint", { max: MAX_PRODUCT_PHOTOS })}</div>
            <Thumbs urls={inputs.product.photos} disabled={!!busy} testPrefix="ads-product-photo"
              onRemove={(i) => setInputs((p) => ({ ...p, product: { ...p.product, photos: p.product.photos.filter((_, n) => n !== i) } }))} />
            {inputs.product.photos.length < MAX_PRODUCT_PHOTOS && (
              <label style={{ ...smallBtn, display: "inline-block", width: "fit-content" }}>
                {t("ads.addProductPhotos")}
                <input type="file" data-consent-tool="ads" accept="image/*" multiple data-testid="ads-product-photos" style={{ display: "none" }}
                  onChange={(e) => { void onProductPhotos(e.target.files); e.target.value = ""; }} />
              </label>
            )}
            <div style={{ fontSize: 12, color: C.green }}>{t("ads.productLockNote")}</div>
          </Section>

          {/* Brand */}
          <Section title={t("ads.brandTitle")}>
            <div style={{ display: "grid", gap: 6, gridTemplateColumns: "1fr 1fr" }}>
              <input value={inputs.brand.name} onChange={(e) => setBrand("name", e.target.value)} placeholder={t("ads.brandName")} data-testid="ads-brand-name" style={inputStyle} />
              <input value={inputs.brand.tagline} onChange={(e) => setBrand("tagline", e.target.value)} placeholder={t("ads.brandTagline")} data-testid="ads-brand-tagline" style={inputStyle} />
              <input value={inputs.brand.price} onChange={(e) => setBrand("price", e.target.value)} placeholder={t("ads.brandPrice")} data-testid="ads-brand-price" style={inputStyle} />
              <input value={inputs.brand.cta} onChange={(e) => setBrand("cta", e.target.value)} placeholder={t("ads.brandCta")} data-testid="ads-brand-cta" style={inputStyle} />
            </div>
            <input value={inputs.brand.url} onChange={(e) => setBrand("url", e.target.value)} placeholder={t("ads.brandUrl")} data-testid="ads-brand-url" style={inputStyle} />
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {inputs.brand.logo_url ? (
                <>
                  <img src={inputs.brand.logo_url} alt="" style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 6, background: C.bg, border: `1px solid ${C.border}` }} />
                  <button type="button" style={smallBtn} onClick={() => setInputs((p) => ({ ...p, brand: { ...p.brand, logo_url: null } }))}>{t("ads.removeLogo")}</button>
                </>
              ) : (
                <label style={{ ...smallBtn, display: "inline-block" }}>
                  {t("ads.addLogo")}
                  <input type="file" data-consent-tool="ads" accept="image/*" data-testid="ads-logo" style={{ display: "none" }} onChange={(e) => { void onLogo(e.target.files); e.target.value = ""; }} />
                </label>
              )}
            </div>
          </Section>

          {/* Feature me */}
          <Section title={t("ads.featureMeTitle")}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" data-testid="ads-feature-me" checked={featureMe}
                onChange={(e) => {
                  setFeatureMe(e.target.checked);
                  if (!e.target.checked) { stopFaceLockWait(); setFailedFaceLockId(null); }
                  // With FaceLocks to cast, the newest is preselected (as the
                  // newest trained face was); otherwise exactly as before.
                  // REQUIRED: never selfies or a slug, only an avatar.
                  const newest = faceLockPickerOn ? castableFaceLocks[0] : null;
                  setInputs((p) => ({
                    ...p,
                    avatar: !e.target.checked ? null
                      : newest ? { selfies: [], artist_slug: "", avatar_id: newest.id }
                      : faceLockRequired ? { selfies: [], artist_slug: "" }
                      : { selfies: p.avatar?.selfies || [], artist_slug: p.avatar ? p.avatar.artist_slug : latestArtist },
                    voice: { ...p.voice, avatar_speaks: e.target.checked ? p.voice.avatar_speaks : false },
                  }));
                }} />
              {faceLockPickerOn || faceLockRequired ? t("faceLock.picker.featureMe") : t("ads.featureMe")}
            </label>
            {featureMe && faceLockRequired && crew && (
              // REQUIRED: the avatar step REPLACES the selfies and the old
              // trained-face select. Pick a ready avatar, or create one right
              // here; a new one trains while the ad waits, and it plans by
              // itself when the avatar is ready.
              <FaceLockCastStep
                crew={crew}
                value={chosenFaceLock ? chosenFaceLock.id : null}
                onChange={(id) => { if (id) { pickFaceLock(id); stopFaceLockWait(); setFailedFaceLockId(null); } }}
                waitingId={waitingFaceLockId || failedFaceLockId}
                onCreated={onFaceLockCreated}
                onNoFace={() => {
                  stopFaceLockWait();
                  setFeatureMe(false);
                  setInputs((p) => ({ ...p, avatar: null, voice: { ...p.voice, avatar_speaks: false } }));
                }}
                noFaceLabel={t("faceLock.required.adsNoFace")}
                intro={t("faceLock.required.adsIntro")}
                waitingNote={(name) => t("faceLock.required.adsWaiting", { name })}
                disabled={!!busy}
                testIdPrefix="ads-avatar-step"
                palette={{ accent: C.green, accentSoft: "rgba(34,197,94,.12)", border: C.border, surface: C.bg, white: C.white, text: C.offwhite, muted: C.muted }}
              />
            )}
            {featureMe && faceLockPickerOn && !faceLockRequired && (
              <FaceLockPicker
                avatars={castableFaceLocks}
                value={chosenFaceLock ? chosenFaceLock.id : null}
                onChange={pickFaceLock}
                disabled={!!busy}
                testIdPrefix="ads-facelock"
                palette={{ accent: C.green, accentSoft: "rgba(34,197,94,.12)", border: C.border, surface: C.bg, white: C.white, text: C.offwhite, muted: C.muted }}
              />
            )}
            {featureMe && !chosenFaceLock && !faceLockRequired && (
              <>
                <div style={{ fontSize: 12, color: C.muted }}>{t("ads.selfiesHint", { min: MIN_SELFIES, max: MAX_SELFIES })}</div>
                <Thumbs urls={inputs.avatar?.selfies || []} disabled={!!busy} testPrefix="ads-selfie"
                  onRemove={(i) => setInputs((p) => ({ ...p, avatar: { selfies: (p.avatar?.selfies || []).filter((_, n) => n !== i), artist_slug: p.avatar?.artist_slug || "" } }))} />
                {(inputs.avatar?.selfies.length || 0) < MAX_SELFIES && (
                  <label style={{ ...smallBtn, display: "inline-block", width: "fit-content" }}>
                    {t("ads.addSelfies")}
                    <input type="file" data-consent-tool="ads" accept="image/*" multiple data-testid="ads-selfies" style={{ display: "none" }} onChange={(e) => { void onSelfies(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
                {!faceLockPickerOn && artists.length > 0 && (
                  <>
                    <select value={inputs.avatar?.artist_slug || ""} data-testid="ads-artist-select" style={inputStyle}
                      onChange={(e) => setInputs((p) => ({ ...p, avatar: { selfies: p.avatar?.selfies || [], artist_slug: e.target.value } }))}>
                      {[...artists].reverse().map((a, i) => (
                        <option key={a.slug} value={a.slug}>
                          {a.latest ? t("ads.myTrainedFace") : t("ads.olderTrainedFace", { n: i })}
                        </option>
                      ))}
                      <option value="">{t("ads.noTrainedArtist")}</option>
                    </select>
                    <div style={{ fontSize: 12, opacity: 0.75 }}>{t("ads.trainedFaceHint")}</div>
                  </>
                )}
              </>
            )}
          </Section>

          {/* Voice */}
          <Section title={t("ads.voiceTitle")} testId="ads-voice">
            <Pills value={inputs.voice.mode} testPrefix="ads-voice" label={(id) => t(`ads.voiceModes.${id}`)}
              options={(["none", "stock", "clone"] as AdVoiceMode[]).map((id) => ({ id, label: id }))}
              onChange={(v: AdVoiceMode) => setInputs((p) => ({ ...p, voice: { ...p.voice, mode: v } }))} />
            {inputs.voice.mode !== "none" && (
              <>
                <div style={{ fontSize: 12, color: C.muted }}>{t("ads.language")}</div>
                <Pills value={inputs.voice.language} testPrefix="ads-lang" label={(id) => t(`ads.languages.${id}`)}
                  options={(["en", "es"] as AdLanguage[]).map((id) => ({ id, label: id }))}
                  onChange={(v: AdLanguage) => setInputs((p) => ({ ...p, voice: { ...p.voice, language: v, voice_id: "" } }))} />
              </>
            )}
            {inputs.voice.mode === "stock" && (
              <select value={inputs.voice.voice_id} data-testid="ads-voice-id" style={inputStyle}
                onChange={(e) => setInputs((p) => ({ ...p, voice: { ...p.voice, voice_id: e.target.value } }))}>
                <option value="">{t("ads.pickVoice")}</option>
                {stockVoices.map((v) => <option key={v.id} value={v.id}>{v.name}{v.note ? ` · ${v.note}` : ""}</option>)}
              </select>
            )}
            {inputs.voice.mode === "clone" && (
              <div style={{ display: "grid", gap: 6 }}>
                {voices.saved.length > 0 && (
                  <select value={inputs.voice.saved_voice_id} data-testid="ads-saved-voice" style={inputStyle} onChange={(e) => pickSavedVoice(e.target.value)}>
                    <option value="">{t("ads.newVoice")}</option>
                    {voices.saved.map((v) => <option key={v.id} value={v.id}>{v.name} · {t(`ads.languages.${v.language === "es" ? "es" : "en"}`)}</option>)}
                  </select>
                )}
                {!inputs.voice.saved_voice_id && (
                  <>
                    <div style={{ fontSize: 12, color: C.muted }}>{t("ads.sampleHint", { min: config.voice.clone_min_seconds, max: config.voice.clone_max_seconds })}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                      {canRecord && !recording && (
                        <button type="button" data-testid="ads-record" style={smallBtn} disabled={!!busy} onClick={() => void startRecording()}>{t("ads.record")}</button>
                      )}
                      {recording && (
                        <button type="button" data-testid="ads-record-stop" style={{ ...smallBtn, borderColor: C.red, color: C.red }} onClick={stopRecording}>
                          {t("ads.stopRecording", { seconds: recording.seconds })}
                        </button>
                      )}
                      <label style={{ ...smallBtn, display: "inline-block" }}>
                        {t("ads.uploadSample")}
                        <input type="file" data-consent-tool="ads" accept="audio/*,video/*" data-testid="ads-voice-upload" style={{ display: "none" }} onChange={(e) => { void onSampleFile(e.target.files); e.target.value = ""; }} />
                      </label>
                      {inputs.voice.sample_url && (
                        <span data-testid="ads-sample-ok" style={{ fontSize: 12, color: C.green }}>
                          {sampleSeconds !== null ? t("ads.sampleReadySeconds", { seconds: sampleSeconds }) : t("ads.sampleReady")}
                        </span>
                      )}
                    </div>
                    {!canRecord && <div style={{ fontSize: 12, color: C.muted }}>{t("ads.micUnavailable")}</div>}
                  </>
                )}
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, cursor: "pointer" }}>
                  <input type="checkbox" data-testid="ads-voice-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>{t("ads.consent")}</span>
                </label>
                {inputs.voice.sample_url && !inputs.voice.saved_voice_id && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <input value={voiceName} onChange={(e) => setVoiceName(e.target.value)} placeholder={t("ads.voiceName")} data-testid="ads-voice-name" style={{ ...inputStyle, flex: 1, minWidth: 120 }} />
                    <button type="button" data-testid="ads-voice-save" style={{ ...smallBtn, opacity: consent && !busy ? 1 : 0.5 }} disabled={!consent || !!busy} onClick={() => void saveVoice()}>
                      {t("ads.saveVoice")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {featureMe && inputs.voice.mode !== "none" && (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" data-testid="ads-avatar-speaks" checked={inputs.voice.avatar_speaks}
                  onChange={(e) => setInputs((p) => ({ ...p, voice: { ...p.voice, avatar_speaks: e.target.checked } }))} />
                {t("ads.avatarSpeaks")}
              </label>
            )}
          </Section>

          {/* Music */}
          <Section title={t("ads.musicTitle")}>
            <Pills value={inputs.music.mode} testPrefix="ads-music" label={(id) => t(`ads.musicModes.${id}`)}
              options={(["none", "upload", ...(config.music.stock.length ? ["stock"] : [])] as AdMusicMode[]).map((id) => ({ id, label: id }))}
              onChange={(v: AdMusicMode) => setInputs((p) => ({ ...p, music: { mode: v, url: v === "upload" ? p.music.url : null, track_id: v === "stock" ? p.music.track_id : null } }))} />
            {inputs.music.mode === "upload" && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ ...smallBtn, display: "inline-block" }}>
                  {t("ads.addTrack")}
                  <input type="file" data-consent-tool="ads" accept="audio/*" data-testid="ads-music-file" style={{ display: "none" }} onChange={(e) => { void onMusicFile(e.target.files); e.target.value = ""; }} />
                </label>
                {inputs.music.url && <span style={{ fontSize: 12, color: C.green }}>{musicName || t("ads.trackReady")}</span>}
              </div>
            )}
            {inputs.music.mode === "stock" && (
              <select value={inputs.music.track_id || ""} data-testid="ads-music-track" style={inputStyle}
                onChange={(e) => setInputs((p) => ({ ...p, music: { ...p.music, track_id: e.target.value || null } }))}>
                <option value="">{t("ads.pickTrack")}</option>
                {config.music.stock.map((tr) => <option key={tr.id} value={tr.id}>{tr.title} · {tr.mood} · {tr.seconds}s</option>)}
              </select>
            )}
          </Section>

          {/* Captions */}
          <Section title={t("ads.captionsTitle")}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" data-testid="ads-captions" checked={inputs.captions} onChange={(e) => set("captions", e.target.checked)} />
              {inputs.voice.mode === "none" ? t("ads.captionsNeedVoice") : t("ads.captions")}
            </label>
          </Section>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <button type="button" onClick={() => void plan()} disabled={!ready} data-testid="ads-plan"
              style={{ borderRadius: 10, border: "none", background: ready ? `linear-gradient(135deg,${C.green},${C.greenDeep})` : "rgba(255,255,255,.08)", color: ready ? C.white : C.muted, fontWeight: 700, letterSpacing: 1, padding: "10px 18px", fontSize: 12, cursor: ready ? "pointer" : "default", fontFamily: "'Rajdhani', sans-serif" }}>
              {t("ads.planMyAd")}
            </button>
            <span data-testid="ads-ready-note" style={{ fontSize: 12, color: C.muted }}>
              {busy ? busy : ready ? t("ads.freeApprove", { credits: credits.toLocaleString() }) : blocker}
            </span>
          </div>
          {note && <div data-testid="ads-note" style={{ fontSize: 13, color: C.offwhite }}>{note}</div>}
        </div>
      )}

      {phase === "planning" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
          <div style={{ color: C.muted, fontSize: 12 }}>{note || t("ads.planningShort")}</div>
          <div style={{ height: 6, borderRadius: 3, background: C.bg, overflow: "hidden", marginTop: 8 }}>
            <div style={{ height: "100%", width: "50%", background: `linear-gradient(90deg,${C.green},${C.gold})`, animation: "ldc-shimmer 1.2s ease-in-out infinite" }} />
          </div>
        </div>
      )}

      {phase === "review" && (
        <div data-testid="ads-review-pointer" style={{ background: C.card, border: `1px solid ${C.green}`, borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, color: C.offwhite, lineHeight: 1.6 }}>{t("ads.reviewLead")}
            <b style={{ color: C.white }}> {t("ads.reviewBold")}</b>
          </div>
          <div style={{ fontSize: 13, color: C.muted }}>{t("ads.nothingCharged")} {t("ads.renderWillCost")} <b style={{ color: C.gold }}>{t("ads.renderCostCredits", { count: credits })}</b>.</div>
          <SiteMarkSwitch testId="ads-site-mark" style={{ color: C.offwhite }} />
          {balance !== null && balance < credits && (
            <div data-testid="ads-balance-low" style={{ fontSize: 13, color: C.red }}>{t("ads.balanceTooLow", { balance: balance.toLocaleString() })}{" "}
              <button type="button" data-testid="ads-buy" onClick={() => openPaySheet({ need: credits, have: balance, label: t("ads.payForRender") })}
                style={{ color: C.gold, fontWeight: 700, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>{t("ads.buyCredits")}</button>
            </div>
          )}
          {/* Quick actions: only knobs the engine honours. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <button type="button" data-testid="ads-chip-start-generation" onClick={() => requestAdCommit()}
              style={{ ...pillStyle(true), background: `linear-gradient(135deg,${C.green},${C.greenDeep})`, border: "none" }}>{t("ads.chips.start")}</button>
            <button type="button" data-testid="ads-chip-shorter" onClick={() => void plan(t("ads.chips.shorterNote"))} style={pillStyle(false)}>{t("ads.chips.shorter")}</button>
            <button type="button" data-testid="ads-chip-longer" onClick={() => void plan(t("ads.chips.longerNote"))} style={pillStyle(false)}>{t("ads.chips.longer")}</button>
            {stockVoiceByGender(allStock, inputs.voice.language, "f") && (
              <button type="button" data-testid="ads-chip-female-narrator" onClick={() => setNarrator("f")} style={pillStyle(false)}>{t("ads.chips.female")}</button>
            )}
            {stockVoiceByGender(allStock, inputs.voice.language, "m") && (
              <button type="button" data-testid="ads-chip-male-narrator" onClick={() => setNarrator("m")} style={pillStyle(false)}>{t("ads.chips.male")}</button>
            )}
            <button type="button" data-testid="ads-chip-plan-again" onClick={() => void plan()} style={pillStyle(false)}>{t("ads.chips.replan")}</button>
          </div>
          {note && <div data-testid="ads-note" style={{ fontSize: 13, color: C.offwhite }}>{note}</div>}
        </div>
      )}

      {phase === "working" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
          <div data-testid="ads-working-note" style={{ color: C.muted, fontSize: 12 }}>{note || t("ads.renderingShort")}</div>
          <div style={{ height: 6, borderRadius: 3, background: C.bg, overflow: "hidden" }}>
            <div style={{ height: "100%", width: "50%", background: `linear-gradient(90deg,${C.green},${C.gold})`, animation: "ldc-shimmer 1.2s ease-in-out infinite" }} />
          </div>
          {jobId && (
            <button type="button" data-testid="ads-cancel" onClick={() => void cancel()} style={{ ...smallBtn, justifySelf: "start" }}>{t("ads.cancel")}</button>
          )}
        </div>
      )}

      {phase === "done" && (
        <div data-testid="ads-done" style={{ background: C.card, border: "1px solid rgba(34,197,94,.35)", borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
          <div>{note}</div>
          {videoUrl && <video src={videoUrl} controls playsInline style={{ width: "100%", maxWidth: 300, borderRadius: 10, border: `1px solid ${C.border}` }} />}
          {/* The link back to this studio, with the sharer's referral code. */}
          <ShareCard studio="ads" mediaUrl={videoUrl || undefined} fileName="ad-spot.mp4" />
          {stills.length > 0 && (
            <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(4, 1fr)" }}>
              {stills.slice(0, 8).map((s) => (
                <a key={s.index} href={s.url} download target="_blank" rel="noreferrer" style={{ display: "block", borderRadius: 6, overflow: "hidden", border: `1px solid ${C.border}` }}>
                  <img src={s.url} alt="" style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }} />
                </a>
              ))}
            </div>
          )}
          <button type="button" data-testid="ads-make-another"
            onClick={() => { setPhase("write"); setVideoUrl(""); setStills([]); setNote(""); setJobId(null); }}
            style={{ justifySelf: "start", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.offwhite, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>
            {t("ads.makeAnother")}
          </button>
        </div>
      )}
    </div>
  );
}
